import React from 'react';
import cx from 'classnames';

import useUniqueId from '../../utils/useUniqueId';
import styles from './ListItem.module.scss';

export interface Props {
  /** CSS className for the ListItem */
  className?: Parameters<typeof cx>[0];
  /** One or more elements to place in the leftmost container within the ListItem */
  leadingContents?: React.ReactNode;
  /** One or more elements to place in the second container from left within the ListItem */
  leadingTextRows?: React.ReactNode;
  /** One or more elements to place in the rightmost/trailing container within the ListItem */
  trailingContentsColumns?: React.ReactNode;
}

export const ListItem: React.FC<Props> = ({
  className,
  leadingTextRows,
  leadingContents,
  trailingContentsColumns,
}) => {
  const leadingContentsBase = useUniqueId('leading-contents');
  const leadingTextRowBase = useUniqueId('leading-text-row');
  const trailingTextColumnBase = useUniqueId('trailing-text-column');

  return (
    <div className={cx(styles.listItem, className)}>
      {leadingContents && (
        <div className={styles.leadingContents}>
          {React.Children.map(leadingContents, (column, idx) => (
            <div
              className={styles.column}
              key={`${leadingContentsBase}-${idx}`}
              data-testid={`${leadingContentsBase}-${idx}`}
            >
              {column}
            </div>
          ))}
        </div>
      )}
      {leadingTextRows && (
        <div className={styles.leadingText}>
          {React.Children.map(
            leadingTextRows,
            (row: React.ReactNode, idx: number) => (
              <div
                className={cx(styles.row, idx === 1 && styles.spaceBetween)}
                key={`${leadingTextRowBase}-${idx}`}
                data-testid={`${leadingTextRowBase}-${idx}`}
              >
                {row}
              </div>
            ),
          )}
        </div>
      )}
      {trailingContentsColumns && (
        <div className={styles.trailingContents}>
          {React.Children.map(
            trailingContentsColumns,
            (col: React.ReactNode, idx: number) => (
              <div
                className={styles.column}
                key={`${trailingTextColumnBase}-${idx}`}
                data-testid={`${trailingTextColumnBase}-${idx}`}
              >
                {col}
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
};

ListItem.displayName = 'ListItem';
export default ListItem;
export { styles };
